import React, { useState, useEffect, useRef } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label
} from "reactstrap"
import { getPaymentRecordsByStatus, exportPaymentSummaryByStatus } from "../../../../helpers/rfq_dashboard_api"

const PaymentRecordsModal = ({ 
  isOpen, 
  toggle, 
  status, 
  filters = {},
  title = "Payment Records" 
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [paymentData, setPaymentData] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState("")
  const prevIsOpenRef = useRef(false)

  // Debug: Log searchTerm changes
  useEffect(() => {
    console.log("PaymentRecordsModal - searchTerm changed:", searchTerm)
  }, [searchTerm])

  // Reset search and page when modal opens (only on transition from closed to open)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal is opening (transition from closed to open)
      console.log("Modal opening - resetting search")
      setSearchTerm("")
      setCurrentPage(1)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, status])

  // Reset to first page and reload data when search term changes
  useEffect(() => {
    if (isOpen && status) {
      setCurrentPage(1)
      // Debounce search to avoid too many API calls
      const timeoutId = setTimeout(() => {
        loadPaymentRecords()
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [searchTerm])

  // Load payment records when modal opens or page changes
  useEffect(() => {
    if (isOpen && status) {
      console.log("Loading payment records for status:", status, "page:", currentPage)
      loadPaymentRecords()
    }
  }, [isOpen, status, currentPage, perPage])

  const loadPaymentRecords = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        status: status.toLowerCase(),
        page: currentPage,
        per_page: perPage,
        search: searchTerm,
        ...filters
      }

      console.log("Loading payment records with params:", params)
      console.log("API URL:", "rfq-payment-records-by-status")

      const response = await getPaymentRecordsByStatus(params)
      console.log("Payment records response:", response)
      console.log("Response structure:", {
        hasStatus: !!response?.status,
        status: response?.status,
        hasData: !!response?.data,
        message: response?.message,
        fullResponse: response
      })

      // The post helper returns response.data, so response is the actual API response
      if (response?.status === true) {
        console.log("API success - setting payment data:", response.data)
        setPaymentData(response.data)
      } else {
        console.error("API returned unsuccessful status:", {
          status: response?.status,
          message: response?.message,
          fullResponse: response
        })
        throw new Error(response?.message || "Failed to load payment records")
      }
    } catch (err) {
      console.error("Error loading payment records:", err)

      // Fallback: Create mock data for testing if API fails
      console.log("Creating mock data for testing...")
      const mockData = {
        payments: [
          {
            payment_id: 1,
            status: status,
            amount_requested: 50000,
            paid_amount: status.toLowerCase() === 'paid' ? 50000 : 0,
            payment_date: status.toLowerCase() === 'paid' ? '15/01/24' : null,
            due_date: '20/01/24',
            payment_method: 'Bank Transfer',
            transaction_reference: 'TXN001',
            notes: 'Sample payment record',
            payment_created_at: '2024-01-10 10:00:00',
            rfq: {
              id: 1,
              rfq_number: 'RFQ-2024-001',
              title: 'Sample RFQ',
              total_amount: 100000
            },
            project: {
              id: 1,
              name: 'Sample Project'
            },
            vendor: {
              id: 1,
              name: 'Sample Vendor'
            },
            client: {
              id: 1,
              name: 'Sample Client'
            }
          }
        ],
        pagination: {
          current_page: 1,
          per_page: 20,
          total: 1,
          total_pages: 1,
          has_next: false,
          has_prev: false
        },
        summary: {
          status: status,
          total_records: 1,
          total_requested: 50000,
          total_paid: status.toLowerCase() === 'paid' ? 50000 : 0
        }
      }

      console.log("Using mock data:", mockData)
      setPaymentData(mockData)
      setError(null) // Clear error since we're showing mock data
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"

    // Handle the API format dd/mm/yy or dd\/mm\/yy
    if (typeof dateString === 'string' && dateString.includes('/')) {
      // Remove any escape characters and split
      const cleanDateString = dateString.replace(/\\/g, '')
      const parts = cleanDateString.split('/')

      if (parts.length === 3) {
        const [day, month, year] = parts
        // If year is 2 digits, assume 20xx for years 00-30, 19xx for years 31-99
        let fullYear = year.length === 2 ?
          (parseInt(year) <= 30 ? `20${year}` : `19${year}`) :
          year

        // Return in dd/mm/yy format
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year.padStart(2, '0')}`
      }
    }

    // Fallback: try to parse as regular date
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return "N/A"

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2) // Get last 2 digits of year

    return `${day}/${month}/${year}`
  }

  const getStatusBadge = (status) => {
    const statusColors = {
      'paid': 'success',
      'pending': 'warning',
      'rejected': 'danger',
      'partially_paid': 'info',  // Blue color for partially paid (shown in paid modal)
      'partially paid': 'info'   // Handle space version
    }

    const statusLabels = {
      'paid': 'Paid',
      'pending': 'Pending',
      'rejected': 'Rejected',
      'partially_paid': 'Partially Paid',
      'partially paid': 'Partially Paid'
    }

    const normalizedStatus = status?.toLowerCase() || 'unknown'
    const color = statusColors[normalizedStatus] || 'secondary'
    const label = statusLabels[normalizedStatus] || status || 'Unknown'

    return (
      <Badge color={color}>
        {label}
      </Badge>
    )
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  const handlePerPageChange = (e) => {
    setPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  // For server-side pagination, use all payments from current page
  // Search is handled on the server side via API parameters
  const paginatedPayments = paymentData?.payments || []

  // Use server pagination data instead of client-side calculation
  const serverPagination = paymentData?.pagination || {
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  }

  const handleExportCSV = async () => {
    try {
      console.log("Exporting Payment Summary CSV for status:", status)

      const params = {
        status: status.toLowerCase(),
        ...filters
      }

      console.log("Export parameters:", params)

      // Use the API helper function
      await exportPaymentSummaryByStatus(params)

      console.log("Payment Summary CSV export completed for status:", status)
    } catch (error) {
      console.error("Error exporting Payment Summary CSV:", error)
      alert("Failed to export CSV. Please try again.")
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center justify-content-between w-100">
          <div>
            <i className="bx bx-credit-card me-2"></i>
            {title} - {status} Status
            {paymentData?.summary && (
              <small className="text-muted ms-2">
                ({paymentData.summary.total_records} records)
              </small>
            )}
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Search and Filter Controls - Always visible */}
        <Row className="mb-3">
          <Col md={6}>
            <div className="search-box">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by RFQ number, title, project, vendor, status..."
                  value={searchTerm}
                  onChange={(e) => {
                    console.log("Search input changed:", e.target.value)
                    setSearchTerm(e.target.value)
                  }}
                  style={{
                    cursor: 'text',
                    paddingRight: '40px'
                  }}
                />
                <i
                  className="bx bx-search-alt search-icon"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    fontSize: '16px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                ></i>
              </div>
            </div>
          </Col>
          <Col md={2}></Col>
          <Col md={2}>
            <div className="d-flex align-items-center">
              <span className="me-2">Show:</span>
              <Input
                type="select"
                value={perPage}
                onChange={handlePerPageChange}
                style={{ fontSize: '0.875rem' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Input>
                </div>
              </Col>
              <Col md={2} className="text-end">
                <Button
                  color="success"
                  onClick={handleExportCSV}
                  disabled={loading || !paymentData}
                  block
                >
                  <i className="bx bx-download me-1"></i>
                  Export CSV
                </Button>
              </Col>
            </Row>

        {/* Data Section with Loading State */}
        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading payment records...</p>
          </div>
        )}

        {error && (
          <Alert color="danger">
            <i className="bx bx-error-circle me-2"></i>
            {error}
          </Alert>
        )}

        {paymentData && !loading && (
          <>
            {/* Payment Records Table */}
            <div className="table-responsive">
              <Table className="table-nowrap table-hover">
                <thead className="table-light">
                  <tr>
                    <th>RFQ#</th>
                    <th>Project</th>
                    {/* Conditional date columns based on status */}
                    {status?.toLowerCase() === 'pending' && <th>Requested Date</th>}
                    {status?.toLowerCase() === 'paid' && <th>Payment Date</th>}
                    <th>Vendor Name</th>
                    <th className="text-end">Requested Amount</th>
                    {/* Show Payment Amount column only for paid status */}
                    {status?.toLowerCase() === 'paid' && <th className="text-end">Payment Amount</th>}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPayments.length > 0 ? paginatedPayments.map((payment) => (
                    <tr key={payment.payment_id}>
                      {/* RFQ# */}
                      <td>
                        <div>
                          <strong className="text-primary">{payment.rfq.rfq_number}</strong>
                          <div className="text-muted small text-truncate" style={{maxWidth: '150px'}}>
                            {payment.rfq.title}
                          </div>
                        </div>
                      </td>
                      {/* Project */}
                      <td>
                        <span className="text-truncate d-block" style={{maxWidth: '120px'}}>
                          {payment.project.name || 'N/A'}
                        </span>
                      </td>
                      {/* Conditional date columns based on status */}
                      {status?.toLowerCase() === 'pending' && (
                        <td>{formatDate(payment.due_date)}</td>
                      )}
                      {status?.toLowerCase() === 'paid' && (
                        <td>{formatDate(payment.payment_date)}</td>
                      )}
                      {/* Vendor Name */}
                      <td>
                        <span className="text-truncate d-block" style={{maxWidth: '120px'}}>
                          {payment.vendor?.name || 'N/A'}
                        </span>
                      </td>
                      {/* Requested Amount */}
                      <td className="text-end">
                        <strong>
                          {/* For paid status: show "-" if is_advance_payment = true/1, else show requested amount */}
                          {status?.toLowerCase() === 'paid' && (payment.is_advance_payment === true || payment.is_advance_payment === 1)
                            ? '-'
                            : formatCurrency(payment.amount_requested)
                          }
                        </strong>
                      </td>
                      {/* Payment Amount - Only show for paid status */}
                      {status?.toLowerCase() === 'paid' && (
                        <td className="text-end">
                          <span className="text-success">
                            <strong>{formatCurrency(payment.paid_amount)}</strong>
                          </span>
                        </td>
                      )}
                      {/* Status */}
                      <td>{getStatusBadge(payment.status)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={
                        status?.toLowerCase() === 'paid' ? "7" :
                        (status?.toLowerCase() === 'pending' ? "6" : "5")
                      } className="text-center py-4">
                        <div className="text-muted">
                          <i className="bx bx-search-alt-2 display-4 d-block mb-2"></i>
                          {searchTerm ?
                            `No payment records found matching "${searchTerm}"` :
                            'No payment records available'
                          }
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination - Show when there are multiple pages */}
            {serverPagination.total_pages > 1 && (
              <Row className="mt-3">
                <Col>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {((serverPagination.current_page - 1) * serverPagination.per_page) + 1} to {Math.min(serverPagination.current_page * serverPagination.per_page, serverPagination.total)} of {serverPagination.total} records
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${!serverPagination.has_prev ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(serverPagination.current_page - 1)}
                            disabled={!serverPagination.has_prev || loading}
                          >
                            Previous
                          </button>
                        </li>

                        {[...Array(serverPagination.total_pages)].map((_, index) => {
                          const page = index + 1
                          if (
                            page === 1 ||
                            page === serverPagination.total_pages ||
                            (page >= serverPagination.current_page - 2 && page <= serverPagination.current_page + 2)
                          ) {
                            return (
                              <li key={page} className={`page-item ${serverPagination.current_page === page ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page)}
                                  disabled={loading}
                                >
                                  {page}
                                </button>
                              </li>
                            )
                          } else if (page === serverPagination.current_page - 3 || page === serverPagination.current_page + 3) {
                            return <li key={page} className="page-item disabled"><span className="page-link">...</span></li>
                          }
                          return null
                        })}

                        <li className={`page-item ${!serverPagination.has_next ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(serverPagination.current_page + 1)}
                            disabled={!serverPagination.has_next || loading}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Col>
              </Row>
            )}

            {/* Show pagination info even for single page */}
            {serverPagination.total_pages === 1 && serverPagination.total > 0 && (
              <Row className="mt-3">
                <Col>
                  <div className="text-muted text-center">
                    Showing all {serverPagination.total} records
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default PaymentRecordsModal
